import { Show, createSignal } from 'solid-js';
import { Dock, type TabId } from '~/components/dock';
import { HackModal } from '~/components/hack-modal';
import { VendorModal } from '~/components/vendor-modal';
import { Screen } from '~/components/screen';
import { StatusBar } from '~/components/status-bar';
import { Deck } from '~/screens/deck';
import { Diag } from '~/screens/diag';
import { MapView } from '~/screens/map-view';
import { MissionList } from '~/screens/mission-list';
import { Op } from '~/screens/op';
import type { GameNode } from '~/lib/nodes';
import type { Target } from '~/types/game';

export function App() {
  const [tab, setTab] = createSignal<TabId>('map');
  const [hackTarget, setHackTarget] = createSignal<Target | null>(null);
  const [vendorNode, setVendorNode] = createSignal<GameNode | null>(null);

  const openHack = (target: Target) => setHackTarget(target);
  const closeHack = () => setHackTarget(null);
  const openVendor = (node: GameNode) => setVendorNode(node);
  const closeVendor = () => setVendorNode(null);

  // Dock navigation always dismisses any open overlay (simple close — no
  // resume; re-tap the node to re-open). Unmounting via the `<Show>` is the
  // SAME teardown path as the modal's own close button, so cleanup runs
  // identically either way.
  const onTabChange = (next: TabId) => {
    closeHack();
    closeVendor();
    setTab(next);
  };

  return (
    <Screen label={`GRID//OS · ${tab().toUpperCase()}`}>
      <StatusBar alerts={tab() === 'map' ? 1 : 0} />

      <div style={{ flex: 1, position: 'relative', display: 'flex', 'flex-direction': 'column', overflow: 'hidden' }}>
        <Show when={tab() === 'map'}>
          <MapView onTargetSelect={openHack} onVendorOpen={openVendor} />
        </Show>
        <Show when={tab() === 'jobs'}>
          <MissionList onEngage={openHack} />
        </Show>
        <Show when={tab() === 'deck'}>
          <Deck />
        </Show>
        <Show when={tab() === 'op'}>
          <Op />
        </Show>
        <Show when={tab() === 'diag'}>
          <Diag />
        </Show>

        <Show when={hackTarget()}>
          <HackModal target={hackTarget()!} onClose={closeHack} />
        </Show>
        <Show when={vendorNode()}>
          <VendorModal node={vendorNode()!} onClose={closeVendor} />
        </Show>
      </div>

      <Dock active={tab()} alertOnMap onChange={onTabChange} />
    </Screen>
  );
}
